import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIdleTimer } from 'react-idle-timer'
import { ButtonDanger, ButtonPrimary } from '../legacy/button'
import { ModalDialog, useModalActionID } from './modal-dialog'
import { useLogout, useRefresh } from '../../context/auth.context'

const IDLE_MS = parseInt(process.env.REACT_APP_TIMEOUT_IDLE_SECONDS, 10) * 1000
const DELAY_MS = parseInt(process.env.REACT_APP_TIMEOUT_DELAY_SECONDS, 10) * 1000

// How often to re-decide from the clock regardless of what any timer is doing.
// Bounds how long a session can outlive its deadline; cheap enough that the
// frequency is set by how much overshoot is acceptable, not by cost.
const SWEEP_MS = 30 * 1000

// The activity list, minus visibilitychange and focus.
//
// Those two are in react-idle-timer's defaults, which means merely RETURNING to
// a tab counts as user activity and re-baselines the idle clock. An unattended
// session was therefore extended by the act of glancing at it — the opposite of
// what an inactivity timeout is for, and it is why a thirty-minute absence came
// back to a fresh five-minute warning rather than a completed sign-out.
const ACTIVITY_EVENTS = [
  'mousemove', 'keydown', 'wheel', 'DOMMouseScroll', 'mousewheel',
  'mousedown', 'touchstart', 'touchmove', 'MSPointerDown', 'MSPointerMove'
]

/**
 * What should happen to a session that has been untouched for `awayMs`.
 *
 * Decided from the clock rather than from whether a timer fired, because a
 * background tab's timers do not run on schedule — Safari throttles them hard,
 * and a sleeping machine stops them altogether. The countdown worker already
 * works this way and documents why; the idle transition did not, so the two
 * halves of the same timeout disagreed about what time it was.
 */
export const idleDecision = (awayMs, idleMs = IDLE_MS, delayMs = DELAY_MS) => {
  if (awayMs >= idleMs + delayMs) return 'logout' // the deadline passed while away
  if (awayMs >= idleMs) return 'warn' // overdue warning, but still inside the grace period
  return 'none'
}

const useWebWorker = workerFunction => {
  let [result, updateResult] = useState(null)
  let [loading, updateLoading] = useState(null)
  let [error, updateError] = useState(null)

  let [worker, updateWorker] = useState()
  let [scriptUrl, updateScriptURL] = useState()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoWorkerFunction = useCallback(workerFunction, [])

  const start = value => {
    try {
      const code = memoWorkerFunction.toString()
      const blob = new Blob([`(${code})()`], { type: 'application/javascript' })
      const scriptUrl = URL.createObjectURL(blob)
      let worker = new Worker(scriptUrl)

      updateWorker(worker)
      updateScriptURL(scriptUrl)

      updateLoading(() => true)

      worker.onmessage = e => {
        updateLoading(() => false)
        updateResult(() => e.data)
      }

      worker.onerror = e => {
        updateLoading(() => false)
        updateError(() => e.error)
      }

      worker.postMessage(value)
    } catch (e) {
      updateLoading(() => false)
      updateError(() => e.message)
    }
  }

  // Stopping a countdown that never started is a no-op. A session found past
  // its deadline on wake signs out without ever starting one, and throwing
  // here used to stop that sign-out from happening at all.
  const stop = () => {
    if (worker) worker.terminate()
    if (scriptUrl) URL.revokeObjectURL(scriptUrl)
  }

  return [start, stop, result, loading, error]
}

const useCountdown = onComplete => {
  // Deadline-based, not a `--value` tick. A decrement counts setInterval fires,
  // which freeze when the tab is backgrounded or the machine sleeps — so on
  // resume it under-reports the elapsed time and the logout may never fire.
  // Posting `deadline - now` each tick means the first post after a resume is
  // the true remaining (0 if already past), triggering an honest logout.
  function workerCountdown() {
    this.onmessage = function(e) {
      var deadline = e.data

      var post = function() {
        postMessage(Math.max(0, Math.round((deadline - Date.now()) / 1000)))
      }

      post()
      setInterval(post, 1000)
    }
  }

  let [start, stop, result, loading, error] = useWebWorker(workerCountdown)

  useEffect(
    () => {
      if (result === 0) {
        onComplete()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [result]
  )

  return [start, stop, result, loading, error]
}

export const Timeout = () => {
  const [countdownStart, countdownStop, countdownValue] = useCountdown(() => handleLogout())

  const logout = useLogout()
  const { refresh } = useRefresh()

  const [modalID, modalShow, modalHide] = useModalActionID()

  // Whether the warning is already up, so the sweep below re-decides without
  // restarting a countdown that is already running.
  const warningRef = useRef(false)

  // The deadline is anchored to the last real activity, NOT to the moment this
  // happened to fire. A throttled timer can fire minutes late, and
  // Date.now() + delay would then hand back grace the session never earned.
  const handleIdle = () => {
    const deadline = lastActiveMs() + IDLE_MS + DELAY_MS

    if (Date.now() >= deadline) {
      handleLogout()
      return
    }

    warningRef.current = true
    countdownStart(deadline)
    pause()
    modalShow()
  }

  const { start, pause, getLastActiveTime } = useIdleTimer({
    timeout: IDLE_MS,
    throttle: 1000,
    events: ACTIVITY_EVENTS,
    onIdle: handleIdle,
    // Activity in ANY admin tab keeps them all alive. Without this, dropping
    // visibilitychange from the event list means a tab left in the background
    // counts down and signs out while its owner is working in the next tab
    // over — and the sign-out is session-wide, so it takes the active tab with
    // it.
    crossTab: true,
    name: 'edxr-admin-idle'
  })

  // Wall clock of the last real interaction. v5 returns a Date; before the timer
  // has seen anything, treat now as the baseline rather than reporting an
  // infinitely old session.
  const lastActiveMs = () => {
    const last = getLastActiveTime()
    return last ? last.getTime() : Date.now()
  }

  // The single question, asked against the only source that cannot drift.
  // Held in a ref so the listeners below bind once and still see current
  // handlers — the same reason the device-login modal holds its reject.
  const evaluateRef = useRef()
  evaluateRef.current = () => {
    const decision = idleDecision(Date.now() - lastActiveMs())

    if (decision === 'logout') {
      handleLogout()
      return
    }

    if (decision === 'warn' && !warningRef.current) handleIdle()
  }

  // Returning to the tab is the moment the decision has to be re-made, because
  // while it was hidden the timer meant to make it was throttled or suspended.
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'visible') evaluateRef.current()
    }
    document.addEventListener('visibilitychange', handler)
    return () => document.removeEventListener('visibilitychange', handler)
  }, [])

  // Belt and braces. Everything above still depends on SOME timer firing:
  // react-idle-timer's, or the countdown worker's. A throttled tab, a Worker the
  // page's CSP refuses to construct from a Blob URL, a wedged event loop — any
  // one of those and the session simply never ends, silently. This asks the
  // clock directly, so the timeout holds even when every timer around it fails.
  useEffect(() => {
    const id = setInterval(() => evaluateRef.current(), SWEEP_MS)
    return () => clearInterval(id)
  }, [])

  const handleContinue = async () => {
    warningRef.current = false
    modalHide()
    countdownStop()
    // Extend the real server session (auth-refresh), not just the client idle
    // timer, so "Stay Signed In" actually keeps you signed in. If the refresh
    // token already expired, refresh throws and we log out honestly instead of
    // leaving a dead session that gets kicked on the next request.
    try {
      await refresh()
      start()
    } catch {
      handleLogout()
    }
  }

  // Set once sign-out starts. The revoke is async and the sweep keeps asking
  // every thirty seconds, so without this one idle session could be signed
  // out several times over.
  const loggingOutRef = useRef(false)

  const handleLogout = () => {
    if (loggingOutRef.current) return
    loggingOutRef.current = true

    warningRef.current = false
    // Tearing down the warning is cleanup; nothing in it may stand between an
    // expired session and its sign-out.
    try {
      modalHide()
      countdownStop()
    } catch (e) {
      console.error(e)
    }
    logout('inactivity')
  }

  const timeout = useMemo(
    () => {
      let min = Math.floor(countdownValue / 60)
      let sec = countdownValue - min * 60

      let minStr = min.toString().padStart(2, 0)
      let secStr = sec.toString().padStart(2, 0)

      return `${minStr}:${secStr}`
    },
    [countdownValue]
  )

  return (
    <React.Fragment>
      <ModalDialog target={modalID} header="Inactivity Warning" backdropStatic hideClose>
        <div className="py-2">
          Due to inactivity you will be signed out in {timeout}.
        </div>
        <div className="py-2">
          <ButtonPrimary text="Stay Signed In" onClick={handleContinue} />
          <ButtonDanger text="Sign Out Now" onClick={handleLogout} />
        </div>
      </ModalDialog>
    </React.Fragment>
  )
}
